import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip';
import AuditInfo from 'components/audit/AuditInfo';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import CustomFieldDisplay from 'components/forms/inputs/custom-fields/CustomFieldDisplay';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import GridItemList, { filterRowList, Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { ReactTable } from 'components/tables/BasicTable';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import CustomFieldCards from 'pages/offices/CustomFieldCards';
import {
    RECORD_GRID_CLASS,
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordMoreMenu,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { translationRows } from 'pages/job-roles/translationRows';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetJobPositionByIdQuery } from 'store/api/jobPositions';
import { ClassifierData } from 'types/classifiers';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { EntityTextDto, JobPositionDto } from 'types/jobPositions';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { JobPositionSection } from './form-sections/JobPositionSection';
import { useDeleteJobPosition } from './useDeleteJobPosition';
import EmployeesView from './views/EmployeesView';

const USED_CLASSIFIERS = ['JOB_POSITION_TYPE', 'LANGUAGE'];
const UPDATE_JOB_POSITION = ['UPDATE.JOB_POSITION'];
const DELETE_JOB_POSITION = ['DELETE.JOB_POSITION'];

/** The job position's read rows by name: the external id and the type show behind their flags. */
const jobPositionRows = (jobPosition: JobPositionDto, classifiers: Record<string, ClassifierData>) =>
    ({
        name: { label: t('name'), content: jobPosition.name || '-' },
        externalId: { label: t('job-positions.external-id'), content: jobPosition.externalId || '-' },
        type: {
            label: t('job-positions.position-type'),
            content: jobPosition.positionTypeCode
                ? getClassifierText(classifiers['JOB_POSITION_TYPE'], jobPosition.positionTypeCode) || '-'
                : '-',
        },
    }) satisfies Record<string, Row>;

const JobPositionPage = (): JSX.Element => {
    const { id: jobPositionId } = useParams();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/job-positions');
    const isNew = useIsNewDesign();
    const canEdit = useHasPermissions(UPDATE_JOB_POSITION);
    const canDelete = useHasPermissions(DELETE_JOB_POSITION);
    const confirmDelete = useDeleteJobPosition(jobPositionId);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: jobPosition, isLoading: isLoadingJobPosition } = useGetJobPositionByIdQuery(
        jobPositionId ?? skipToken,
        { refetchOnMountOrArgChange: true }
    );
    const isDraft = !!jobPosition?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/job-positions');
    const draftActions = useDraftActions({
        draft: jobPosition?.isDraft ? { type: 'job-position', id: jobPosition.id } : null,
        createRoute: '/job-position/new',
        onDeleted: leaveDeleted,
    });
    const missingFields = draftActions.draft?.missingFields;
    const {
        classifiers,
        allClassifiers,
        isLoading: isLoadingClassifiers,
    } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const {
        customFields,
        customFieldsFiltered,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.JOB_POSITION,
        fields: jobPosition?.customFields,
    });

    const isJobPositionExternalIdEnabled = useIsFeatureEnabled(FeatureFlag.JOB_POSITION_EXTERNAL_ID_ENABLED);
    const isJobPositionTypeEnabled = useIsFeatureEnabled(FeatureFlag.JOB_POSITION_TYPE);

    const rows = useMemo(
        () => (jobPosition ? jobPositionRows(jobPosition, classifiers) : undefined),
        [jobPosition, classifiers]
    );
    const generalRows = useMemo<Row[]>(() => {
        if (!rows) return [];
        const list: Row[] = [rows.name];
        if (isJobPositionExternalIdEnabled) {
            list.push(rows.externalId);
        }
        if (isJobPositionTypeEnabled) {
            list.push(rows.type);
        }
        customFieldsFiltered.forEach((field) => {
            list.push({
                label: field.label,
                content: <CustomFieldDisplay key={field.id} field={field} classifiers={allClassifiers} />,
            });
        });
        return withMissing(list, missingFields);
    }, [
        rows,
        allClassifiers,
        customFieldsFiltered,
        isJobPositionExternalIdEnabled,
        isJobPositionTypeEnabled,
        missingFields,
    ]);

    const translations = useMemo(
        () => (jobPosition?.entityTexts ?? []).filter((el) => el.value || el.textMale || el.textFemale),
        [jobPosition]
    );

    const translationColumns = useMemo<DataTableColumnDef<EntityTextDto>[]>(
        () => [
            {
                id: 'language',
                header: t('translations.language'),
                accessorFn: (row) => getClassifierText(classifiers['LANGUAGE'], row.langCode),
                meta: { width: '25%' },
            },
            {
                id: 'text',
                header: t('translations.text'),
                accessorFn: (row) => row.value || '-',
                meta: { width: '25%' },
            },
            {
                id: 'textMale',
                header: t('translations.text-male'),
                accessorFn: (row) => row.textMale || '-',
                meta: { width: '25%' },
            },
            {
                id: 'textFemale',
                header: t('translations.text-female'),
                accessorFn: (row) => row.textFemale || '-',
                meta: { width: '25%' },
            },
        ],
        [classifiers]
    );

    const tabs = useMemo<TabType[]>(() => {
        if (!jobPosition || !rows) return [];
        const auditInfo = (
            <AuditInfo
                createdAt={jobPosition.createdAt}
                createdByEmployee={jobPosition.createdByEmployee}
                lastModifiedAt={jobPosition.lastModifiedAt ?? undefined}
                changedByEmployee={jobPosition.changedByEmployee}
            />
        );
        // The card's rows: the external id is set on create only, so no field of the card edits it.
        const positionRows = withMissing(
            filterRowList([
                rows.name,
                isJobPositionExternalIdEnabled ? { ...rows.externalId, readOnly: true } : null,
                isJobPositionTypeEnabled ? rows.type : null,
            ]),
            missingFields
        );
        return [
            {
                id: 'general',
                label: t('matters.general'),
                component: isNew ? (
                    <div className={RECORD_GRID_CLASS}>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            <JobPositionSection
                                id="job-position"
                                titleId="job-positions.job-position"
                                jobPosition={jobPosition}
                                rows={positionRows}
                            >
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="job-positions.job-position"
                                        data={positionRows}
                                        button={edit}
                                    />
                                )}
                            </JobPositionSection>
                            {customFieldsFiltered.length > 0 ? (
                                <JobPositionSection
                                    id="custom-fields"
                                    titleId="custom-fields.title"
                                    jobPosition={jobPosition}
                                >
                                    {(edit) => (
                                        <CustomFieldCards
                                            fields={customFields}
                                            classifiers={allClassifiers}
                                            edit={edit}
                                        />
                                    )}
                                </JobPositionSection>
                            ) : null}
                            {auditInfo}
                        </div>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            {isDraft ? <DraftSharedWith /> : null}
                            <JobPositionSection
                                id="translations"
                                titleId="job-positions.position-name-translations"
                                jobPosition={jobPosition}
                            >
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="job-positions.position-name-translations"
                                        data={translationRows(classifiers, jobPosition.entityTexts)}
                                        button={edit}
                                    />
                                )}
                            </JobPositionSection>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        <GridItemList titleId="matters.overview" data={generalRows} />
                        {isDraft ? <DraftSharedWith /> : null}
                        {translations.length > 0 ? (
                            <MainCard content={false}>
                                <ReactTable<EntityTextDto>
                                    aria-label={t('job-positions.position-name-translations')}
                                    columns={translationColumns}
                                    data={translations}
                                />
                            </MainCard>
                        ) : null}
                        {auditInfo}
                    </div>
                ),
            },
            {
                id: 'employees',
                label: t('employees.employees'),
                // Nobody is employed in a draft position.
                hidden: isDraft,
                component: <EmployeesView jobPositionEmployees={jobPosition.jobPositionEmployees ?? []} />,
            },
        ];
    }, [
        jobPosition,
        rows,
        generalRows,
        translations,
        translationColumns,
        isNew,
        classifiers,
        allClassifiers,
        customFields,
        customFieldsFiltered,
        isJobPositionExternalIdEnabled,
        isJobPositionTypeEnabled,
        isDraft,
        missingFields,
    ]);

    if (isLoadingJobPosition || isLoadingClassifiers || isLoadingCustomFieldData) {
        return <Loader />;
    }

    if (!jobPosition) {
        return <></>;
    }

    const tabsGallery = (
        <TabsGallery
            tabs={tabs}
            tabGroupId="job-position-page"
            trackTabsInHistoryState
            usePathBasedRouting
            wrapperClassName={RECORD_TABS_CLASS}
            panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
            className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
        />
    );

    const draft = draftActions.draft;
    const title = jobPosition.name || (isDraft ? t('new-entity.job-position') : '-');
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;

    if (isNew) {
        const typeLabel =
            isJobPositionTypeEnabled && jobPosition.positionTypeCode
                ? getClassifierText(classifiers['JOB_POSITION_TYPE'], jobPosition.positionTypeCode)
                : '';
        const header = (
            <RecordHeader
                number={isJobPositionExternalIdEnabled ? jobPosition.externalId : undefined}
                chips={
                    isDraft || typeLabel ? (
                        <>
                            {isDraft ? <DraftChip /> : null}
                            {typeLabel ? <StatusChip type="neutral" label={typeLabel} /> : null}
                        </>
                    ) : undefined
                }
                title={title}
                pin={isDraft ? undefined : { kind: 'job-position', id: jobPosition.id }}
                meta={draft ? <DraftMeta draft={draft} /> : undefined}
                actions={
                    isDraft ? (
                        <DraftRecordActions actions={draftActions} />
                    ) : (
                        // Delete moves here from the edit form, with its gate.
                        <RecordMoreMenu
                            menuItems={
                                canDelete
                                    ? [{ handler: confirmDelete, title: t('delete'), variant: 'destructive' }]
                                    : []
                            }
                        />
                    )
                }
            />
        );
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage
                    header={
                        draftNotice ? (
                            <div className="flex flex-col gap-4">
                                {header}
                                {draftNotice}
                            </div>
                        ) : (
                            header
                        )
                    }
                >
                    <SectionEditProvider canEdit={canEdit && !isDraft}>{tabsGallery}</SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    return (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout className="flex h-full flex-col overflow-hidden">
                <PageHeader
                    title={title}
                    showBreadcrumb
                    onBack={goBack}
                    buttons={
                        isDraft ? (
                            <div className="flex items-center justify-end gap-2">
                                <DraftRecordActions actions={draftActions} />
                            </div>
                        ) : (
                            <PermissionButton
                                permissions={UPDATE_JOB_POSITION}
                                variant="outlined"
                                size="small"
                                onClick={() => navigateWithReturn(`/job-position/${jobPositionId}/edit`)}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        )
                    }
                >
                    {draft ? (
                        <div className="flex flex-wrap items-center gap-2 py-3">
                            <DraftChip />
                            <DraftMeta draft={draft} />
                        </div>
                    ) : null}
                </PageHeader>
                {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
                {tabsGallery}
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );
};

export default JobPositionPage;
